import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Eye, Trash2, Filter, Calendar, DollarSign, Users, Package, FileText, CheckCircle, AlertCircle, Upload, X, Copy, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getContracts, copyContract, deleteContract, downloadExport } from '../../config/api';
import ContractCSVUpload from '../../components/ContractCSVUpload';
import { useTableSort, SortTh } from '../../components/ui/tableSort';

// Column -> value accessor for client-side sorting (module-level = stable).
const CONTRACT_SORTS = {
  number: (c) => c.number,
  status: (c) => c.status,
  effective: (c) => (c.effectiveFrom ? new Date(c.effectiveFrom).getTime() : null),
  gpo: (c) => c.gpo?.name,
  metrics: (c) => (c._count?.chargebackClaims ?? 0),
};

const Contracts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasRole } = useAuth();
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterGPO, setFilterGPO] = useState('ALL');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  
  // Export contracts to Excel
  const exportToExcel = async () => {
    try {
      await downloadExport('/api/contracts/export', `contracts-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Contracts exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export contracts to Excel');
    }
  };
  
  // Fetch contracts from API
  const { data: contractsData, isLoading, error } = useQuery({
    queryKey: ['contracts'],
    queryFn: getContracts,
    enabled: !!user,
    retry: 3
  });

  const contracts = contractsData?.contracts || [];
  const pagination = contractsData?.pagination || { total: 0, pages: 0, page: 1 };

  // Copy contract mutation
  const copyMutation = useMutation({
    mutationFn: ({ id, copyData }) => copyContract(id, copyData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract copied successfully!');
      navigate(`/app/contracts/${data.contract.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to copy contract');
    }
  });

  // Delete contract mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => deleteContract(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(`Contract deleted successfully! Removed ${data.deletedData.prices} prices, ${data.deletedData.customers} customers, and ${data.deletedData.aliases} aliases.`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete contract');
    }
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('ALL');
    setFilterStatus('ALL');
    setFilterGPO('ALL');
  };

  const handleCopyContract = (contract) => {
    const newNumber = prompt(`Enter new contract number for "${contract.number}":`, `${contract.number}-COPY`);
    if (newNumber && newNumber.trim()) {
      const copyData = {
        number: newNumber.trim(),
        description: `${contract.description} (Copy)`
      };
      copyMutation.mutate({ id: contract.id, copyData });
    }
  };

  const handleDeleteContract = (contract) => {
    if (window.confirm(`Are you sure you want to delete contract "${contract.number}"?\n\nThis will permanently delete:\n- ${contract._count?.prices || 0} product prices\n- ${contract._count?.contractCustomers || 0} customer relationships\n- ${contract._count?.aliases || 0} product aliases\n- All contract data\n\nThis action cannot be undone.`)) {
      deleteMutation.mutate(contract.id);
    }
  };

  const activeFilterCount = [
    searchTerm,
    filterType !== 'ALL',
    filterStatus !== 'ALL',
    filterGPO !== 'ALL'
  ].filter(Boolean).length;

  // Apply filters
  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const matchesSearch = !searchTerm ||
        contract.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.class?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'ALL' || contract.type === filterType;
      const matchesStatus = filterStatus === 'ALL' || contract.status === filterStatus;
      const matchesGPO = filterGPO === 'ALL' ||
        (filterGPO === 'NO_GPO' ? !contract.gpo : contract.gpo?.name === filterGPO);

      return matchesSearch && matchesType && matchesStatus && matchesGPO;
    });
  }, [contracts, searchTerm, filterType, filterStatus, filterGPO]);

  // Client-side column sorting over the filtered set.
  const { sorted: sortedContracts, sort, toggleSort } = useTableSort(filteredContracts, CONTRACT_SORTS);

  // Distinct GPO names present in the loaded contracts, for the filter dropdown.
  const gpoOptions = useMemo(() => {
    const names = new Set((contracts || []).map(c => c.gpo?.name).filter(Boolean));
    return Array.from(names).sort();
  }, [contracts]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800';
      case 'ARCHIVED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'DIRECT':
        return 'bg-blue-100 text-blue-800';
      case 'INDIRECT':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contracts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage contracts, pricing, and customer relationships
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            title="Export Excel"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </button>
          <button
            onClick={() => setShowCSVUpload(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            title="Upload CSV"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload CSV
          </button>
          <button
            onClick={() => navigate('/app/contracts/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            title="New Contract"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Contract
          </button>
        </div>
      </div>

      {/* Contract Analytics Summary */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-blue-500 text-white">
                  <FileText className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Contracts</dt>
                  <dd className="text-lg font-medium text-gray-900">{contracts.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-green-500 text-white">
                  <CheckCircle className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {contracts.filter(c => c.status === 'ACTIVE').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-purple-500 text-white">
                  <Users className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Customers</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {contracts.reduce((sum, c) => sum + (c._count?.contractCustomers || 0), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-orange-500 text-white">
                  <DollarSign className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Prices</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {contracts.reduce((sum, c) => sum + (c._count?.prices || 0), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-indigo-500 text-white">
                  <FileText className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Aliases</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {contracts.reduce((sum, c) => sum + (c._count?.aliases || 0), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-red-500 text-white">
                  <Calendar className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Claims</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {contracts.reduce((sum, c) => sum + (c._count?.chargebackClaims || 0), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                {activeFilterCount} active
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
              title="Toggle advanced filters"
            >
              {showAdvancedFilters ? 'Hide' : 'Show'} Advanced
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
                title="Clear All"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search contracts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Contract Type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">All Types</option>
            <option value="DIRECT">Direct</option>
            <option value="INDIRECT">Indirect</option>
          </select>
          
          {/* Status */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="EXPIRED">Expired</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            {/* GPO Filter */}
            <select
              value={filterGPO}
              onChange={(e) => setFilterGPO(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All GPOs</option>
              <option value="NO_GPO">No GPO</option>
              {gpoOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contracts...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Contracts</h3>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load contracts'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            title="Retry"
          >
            Retry
          </button>
        </div>
      )}

      {/* Contracts Table */}
      {!isLoading && !error && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Contracts ({filteredContracts.length})</h3>
          </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="Contract Details" sortKey="number" sort={sort} onSort={toggleSort} />
                <SortTh label="Type & Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortTh label="Effective Dates" sortKey="effective" sort={sort} onSort={toggleSort} />
                <SortTh label="GPO" sortKey="gpo" sort={sort} onSort={toggleSort} />
                <SortTh label="Metrics" sortKey="metrics" sort={sort} onSort={toggleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedContracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900 break-words">
                        {contract.number}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {contract.description}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Class: {contract.class} • Group: {contract.group}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(contract.type)}`}>
                        {contract.type}
                      </span>
                      <br />
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(contract.effectiveFrom)}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        to {formatDate(contract.effectiveTo)}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {contract.gpo?.name ? (
                        <Link to="/app/gpos" className="text-blue-600 hover:text-blue-800 hover:underline">{contract.gpo.name}</Link>
                      ) : contract.gpoId ? (
                        <span className="text-gray-500">Linked GPO</span>
                      ) : (
                        <span className="text-gray-400 italic">No GPO</span>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 space-y-1">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-blue-500" />
                        <span className="font-medium">{contract._count?.contractCustomers || 0}</span>
                        <span className="text-gray-500">customers</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4 text-green-500" />
                        <span className="font-medium">{contract._count?.prices || 0}</span>
                        <span className="text-gray-500">prices</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4 text-purple-500" />
                        <span className="font-medium">{contract._count?.aliases || 0}</span>
                        <span className="text-gray-500">aliases</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-orange-500" />
                        <span className="font-medium">{contract._count?.chargebackClaims || 0}</span>
                        <span className="text-gray-500">claims</span>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/app/contracts/${contract.id}`)}
                        className="text-primary-600 hover:text-primary-900 p-1"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/app/contracts/${contract.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/app/contracts/${contract.id}/pricing`)}
                        className="text-green-600 hover:text-green-900 p-1"
                        title="Manage Pricing"
                      >
                        <DollarSign className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/app/contracts/${contract.id}/customers`)}
                        className="text-purple-600 hover:text-purple-900 p-1"
                        title="Manage Customers"
                      >
                        <Users className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/app/contracts/${contract.id}/aliases`)}
                        className="text-orange-600 hover:text-orange-900 p-1"
                        title="Manage Aliases"
                      >
                        <Package className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCopyContract(contract)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Copy Contract"
                        disabled={copyMutation.isPending}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      {hasRole('ADMIN') && (
                        <button
                          onClick={() => handleDeleteContract(contract)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete Contract"
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Empty State */}
      {filteredContracts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No contracts found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || activeFilterCount > 0 
              ? 'Try adjusting your search or filters.'
              : 'Get started by creating your first contract.'
            }
          </p>
          {!searchTerm && activeFilterCount === 0 && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/app/contracts/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                title="New Contract"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Contract
              </button>
            </div>
          )}
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upload Contracts via CSV</h3>
                <button
                  onClick={() => setShowCSVUpload(false)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <ContractCSVUpload 
                onUploadComplete={(results) => {
                  setShowCSVUpload(false);
                  // Refresh contracts data
                  queryClient.invalidateQueries({ queryKey: ['contracts'] });
                  
                  // Show summary toast
                  const successCount = results.filter(r => r.success).length;
                  const errorCount = results.filter(r => !r.success).length;
                  
                  if (successCount > 0) {
                    toast.success(`Successfully uploaded ${successCount} contract file(s)`);
                  }
                  if (errorCount > 0) {
                    toast.error(`${errorCount} file(s) failed to upload`);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
